import React, { useState } from 'react';
import Table from 'react-bootstrap/Table';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';
import Swal from '../../messageBox/customSweetAlert';
import { FTP_AUTH_URL } from '../../constant';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../redux/loaderAction';
import SaveButton from '../buttons/SaveButton';

function FTPCredentialsTable({ tableData, setTableData, isLoading }) {
  const dispatch = useDispatch();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [selectedCredentialId, setSelectedCredentialId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({});
  const [savingPassword, setSavingPassword] = useState(false);

  const resetPasswordForm = () => {
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
  };

  const openPasswordModal = (row) => {
    if (row?.id == null) return;
    setSelectedCredentialId(row.id);
    resetPasswordForm();
    setPasswordModalVisible(true);
  };

  const closePasswordModal = () => {
    setPasswordModalVisible(false);
    setSelectedCredentialId(null);
    resetPasswordForm();
  };

  const validatePasswordForm = () => {
    const next = {};
    const trimmedNew = (newPassword ?? '').trim();
    const trimmedConfirm = (confirmPassword ?? '').trim();

    if (!trimmedNew) {
      next.newPassword = 'Enter New Password is required.';
    }
    if (!trimmedConfirm) {
      next.confirmPassword = 'Confirm Password is required.';
    }
    if (trimmedNew && trimmedConfirm && trimmedNew !== trimmedConfirm) {
      next.confirmPassword = 'Passwords do not match.';
    }

    setPasswordErrors(next);
    return Object.keys(next).length === 0;
  };

  const trimmedNewPassword = (newPassword ?? '').trim();
  const trimmedConfirmPassword = (confirmPassword ?? '').trim();
  const passwordsMatch =
    trimmedNewPassword !== '' &&
    trimmedConfirmPassword !== '' &&
    trimmedNewPassword === trimmedConfirmPassword;
  const canSavePassword =
    !savingPassword && trimmedNewPassword !== '' && passwordsMatch;

  const handleSavePassword = async (e) => {
    e?.preventDefault?.();
    if (savingPassword) return;
    if (!validatePasswordForm()) return;
    if (selectedCredentialId == null) return;

    setSavingPassword(true);
    try {
      const response = await axios.patch(
        `${FTP_AUTH_URL}/${selectedCredentialId}`,
        { password: trimmedNewPassword },
        { headers: { 'Content-Type': 'application/json' } },
      );

      const isOk =
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data?.status === 200 ||
        response?.data?.status === '200' ||
        response?.data?.success === true;

      if (isOk) {
        Swal.fire({
          title: 'Success!',
          text: response?.data?.message || 'Password updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        closePasswordModal();
      } else {
        Swal.fire({
          title: 'Failed!',
          text: response?.data?.message || 'Failed to update password.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update password.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteClick = (row) => {
    const id = row?.id;
    if (id == null) return;

    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this FTP credential!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      dispatch(setLoading(true));
      try {
        const response = await axios.delete(`${FTP_AUTH_URL}/${id}`);
        const isOk =
          response?.status === 200 ||
          response?.status === 204 ||
          response?.data?.status === 200 ||
          response?.data?.status === '200' ||
          response?.data?.success === true;

        if (isOk) {
          if (typeof setTableData === 'function') {
            setTableData((prev) =>
              (prev ?? []).filter((item) => item?.id !== id),
            );
          }
          Swal.fire({
            title: 'Success!',
            text:
              response?.data?.message || 'FTP credential deleted successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        } else {
          Swal.fire({
            title: 'Failed!',
            text: response?.data?.message || 'Failed to delete FTP credential.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to delete FTP credential.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        dispatch(setLoading(false));
      }
    });
  };

  return (
    <>
      <Table striped bordered hover className="tbl-require">
        <thead>
          <tr>
            <th>#</th>
            <th>Host Name</th>
            <th>Username</th>
            <th>FTP Type</th>
            <th>Port Number</th>
            <th style={{ textAlign: 'center' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData && tableData.length > 0 ? (
            tableData.map((row, index) => (
              <tr key={row?.id ?? index}>
                <td>{index + 1}</td>
                <td>{row?.host_name ?? 'N/A'}</td>
                <td>{row?.username ?? 'N/A'}</td>
                <td>{row?.ftp_type ?? 'N/A'}</td>
                <td>{row?.port_number ?? 'N/A'}</td>
                <td style={{ textAlign: 'center', cursor: 'pointer' }}>
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2"
                    style={{ fontSize: '14px', textDecoration: 'none' }}
                    onClick={() => openPasswordModal(row)}
                  >
                    Change Password
                  </button>
                  <i
                    className="pi pi-trash icon-btn-delete"
                    title="Delete"
                    onClick={() => handleDeleteClick(row)}
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                {isLoading ? 'Loading...' : 'No FTP credentials found'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Dialog
        header="Change Password"
        visible={passwordModalVisible}
        onHide={closePasswordModal}
        style={{ width: '34rem', maxWidth: '95vw' }}
        modal
        draggable={false}
        closable={!savingPassword}
      >
        <form onSubmit={handleSavePassword}>
          <div className="mb-3">
            <label className="require-field d-block mb-1">
              Enter New Password
            </label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (passwordErrors.newPassword || passwordErrors.confirmPassword) {
                  setPasswordErrors({});
                }
              }}
              disabled={savingPassword}
            />
            {passwordErrors.newPassword && (
              <span className="errors">{passwordErrors.newPassword}</span>
            )}
          </div>
          <div className="mb-3">
            <label className="require-field d-block mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (passwordErrors.confirmPassword) {
                  setPasswordErrors((prev) => ({
                    ...prev,
                    confirmPassword: undefined,
                  }));
                }
              }}
              disabled={savingPassword}
            />
            {passwordErrors.confirmPassword && (
              <span className="errors">{passwordErrors.confirmPassword}</span>
            )}
          </div>
          <div
            className="d-flex justify-content-center gap-2 pt-2"
            style={{ borderTop: '1px solid #eee' }}
          >
            <SaveButton
              type="submit"
              buttonTitle="SAVE"
              loading={savingPassword}
              loadingText="Saving..."
              disabled={!canSavePassword}
            />
            <button
              type="button"
              className="btn rounded-1"
              style={{ backgroundColor: '#f9ae3b', color: '#fff' }}
              onClick={closePasswordModal}
              disabled={savingPassword}
            >
              CLOSE
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export default FTPCredentialsTable;
